import React, { useMemo } from 'react';
import { Result } from 'antd';
import type { ResultProps as AntResultProps } from 'antd';
import type { BaseComponentProps } from '../../types/common';
import { useAndStore } from '../../base/store/useAndStore';
import { mergeStoreProps } from '../../base/storePropsUtils';

export interface AndResultProps extends AntResultProps, BaseComponentProps {
  /**
   * 兼容 server 导出器的 `getPropsName(): "resultProps"` 结构。
   * - 若传入，则与直接 props 合并后作为 Result 的最终 props。
   * - 运行态仍可通过 `setProps(sessionId, id, {...})` 覆盖更新。
   */
  resultProps?: Partial<AntResultProps> & Record<string, any>;
}

export const AndResult: React.FC<AndResultProps> = ({
  id,
  sessionId,
  develop,
  children,
  resultProps,
  ...restProps
}) => {
  // 订阅本组件的 props 切片，setProps(sessionId, id, props) 更新时触发重渲染
  const storeProps = useAndStore((state) =>
    sessionId && id ? state.propsStore[`${sessionId}_props`]?.[id] ?? null : null
  );

  const mergedRestProps = useMemo(() => {
    // 直接 props 与 resultProps 合并（resultProps 优先覆盖 restProps）
    const base = {
      ...(restProps as Record<string, any>),
      ...(resultProps && typeof resultProps === 'object' ? (resultProps as Record<string, any>) : {}),
    } as Record<string, any>;
    // 最后再合并 storeProps（storeProps 优先级最高）
    return mergeStoreProps(base, storeProps ?? undefined);
  }, [restProps, resultProps, storeProps]);

  return <Result {...(mergedRestProps as AntResultProps)}>{children}</Result>;
};

export default AndResult;
